import React, { useState, useEffect } from 'react';

import Avatars from 'components/Avatars';
import Card from 'components/Card';
import CollapsibleContent from 'components/CollapsibleContent';
import CollapsibleText from 'components/CollapsibleText';
import Divider from 'components/Divider';
import Grid from 'components/Grid';
import Html from 'components/Html';
import HtmlCollapsibleText from 'components/HtmlCollapsibleText';
import Link from 'components/Link';
import Typography from 'components/Typography';
import { useEventContext } from 'context/EventContext';
import useViewport from 'hooks/useViewport';
import { handleScrollTo } from 'screens/compare/components/NavigationList/helpers';

import Alternatives from './components/Alternatives';
import Pricing from './components/Pricing';
import ProsAndCons from './components/ProsAndCons';

import classes from './KeyTakeaways.module.scss';

const LIMIT_OVERVIEW_LENGTH = 250;
const MOBILE_OVERVIEW_MAX_HEIGHT = 330;
const MOBILE_BENEFITS_MAX_HEIGHT = 230;
const RIGHT_COL_HEIGHT = 614;
const MAX_REVIEWERS = 3;

const addAvatarsFromSnippets = (reviewers, snippets) => {
  if (!snippets) return;

  for (let i = 0; i < snippets.length; i++) {
    const avatar = snippets[i]?.avatar;
    if (avatar && reviewers.length < MAX_REVIEWERS) {
      reviewers.push(avatar);
    }
    if (reviewers.length >= MAX_REVIEWERS) break;
  }
};
const getAvatarsFromSnippets = (pros, cons) => {
  const reviewers = [];
  for (let i = 0; i < pros.length && reviewers.length < MAX_REVIEWERS; i++) {
    addAvatarsFromSnippets(reviewers, pros[i].snippets);
  }
  if (cons && reviewers.length < MAX_REVIEWERS) {
    for (let i = 0; i < cons.length && reviewers.length < MAX_REVIEWERS; i++) {
      addAvatarsFromSnippets(reviewers, cons[i].snippets);
    }
  }
  // Fill remaining slots with null avatars
  while (reviewers.length < MAX_REVIEWERS) {
    reviewers.push(null);
  }
  return reviewers.reverse();
};

const KeyTakeaways = ({ listing, showPplPricingBanner }) => {
  const [showMore, setShowMore] = useState({
    overview: false,
    benefits: false,
  });
  const [maxHeight, setMaxHeight] = useState({
    overview: RIGHT_COL_HEIGHT * 0.6 - 40,
    benefits: RIGHT_COL_HEIGHT * 0.4 - 40,
  });
  const { sendEvent } = useEventContext();
  const { down, up } = useViewport();
  const rightColumnRef = React.useRef();
  const hasKeyTakeaways = !!listing.key_takeaways_summary;
  const keyTakeaway = listing.key_takeaways_summary;
  const keyTakeawaySummary = keyTakeaway?.summary;
  const hasKeyTakeawaySummary =
    hasKeyTakeaways && keyTakeawaySummary.length > 0;
  const hasKeyTakeawayAiSummary =
    hasKeyTakeawaySummary && keyTakeaway.key_takeaways.length > 0;
  const hasOnlyUseCasesSummary =
    hasKeyTakeawaySummary &&
    keyTakeaway.key_takeaways.length === 1 &&
    keyTakeaway.key_takeaways[0].title === 'Users and use cases';
  const alternatives = listing.is_premium
    ? listing.alternatives_comparisons
    : listing.alternatives_by_section.faq_contenders;
  const hasReviewsSummary = !!listing.reviews_summary;
  const pros = hasReviewsSummary
    ? listing.reviews_summary.topics
        ?.filter((t) => t.type === 'PRO')
        ?.slice(0, 3)
    : listing.prosCons.pros.slice(0, 3);
  const cons = hasReviewsSummary
    ? listing.reviews_summary.topics
        ?.filter((t) => t.type === 'CON')
        ?.slice(0, 3)
    : listing.prosCons.cons.slice(0, 3);
  const hasProsAndCons = !!pros.length && !!cons.length;

  const KEY_TAKEAWAYS_SECTIONS = [
    {
      title: 'Users and use cases',
      key: 'users-and-use-cases',
      displayText: `Who uses ${listing.name}?`,
      id: 'users-and-use-cases',
    },
    {
      title: 'Value for money',
      key: 'pricing',
      displayText: `What do users say about ${listing.name} pricing?`,
      id: 'pricing',
    },
    {
      title: 'Integrations',
      key: 'integrations',
      displayText: `What are the most popular integrations for ${listing.name}?`,
      id: 'integrations',
    },
  ];

  const reviewerUrls = getAvatarsFromSnippets(
    listing.prosCons.pros.slice(0, 3),
    listing.prosCons.cons.slice(0, 3)
  );

  useEffect(() => {
    const height = rightColumnRef?.current?.clientHeight || RIGHT_COL_HEIGHT;

    const overviewMaxHeight = listing.benefits ? height * 0.6 : height;
    const benefitsMaxHeight = height * 0.4;

    setMaxHeight({ overview: overviewMaxHeight, benefits: benefitsMaxHeight });
  }, [listing.benefits]);

  const toggleShowMore = (name) => {
    setShowMore((state) => ({ ...state, [name]: !state[name] }));
  };

  const renderKeyTakeaways = keyTakeaway?.key_takeaways.map((item) => {
    const content = KEY_TAKEAWAYS_SECTIONS.find(
      (section) => section.title === item.title
    );

    if (!content) return '';

    return (
      <Grid item={true} xs={12} key="">
        <div className={classes.keyTakeawayContainer}>
          <Typography variant="heading-md" className={classes.questionTitle}>
            {content.displayText}
          </Typography>
          <Link
            variant="tight-xxs"
            onClick={() => {
              sendEvent({
                name: 'engagement_product_click',
                engagementComponent: 'product_overview',
                engagementDetails: `textlink_see-${content.key}-details`,
                engagementDestination: `product_${content.key}`,
              });
              handleScrollTo(content.id, 100);
            }}
          >
            See details
          </Link>
        </div>
        <Typography variant="tight-xs" className={classes.overviewSubTitle}>
          {item.summary}
        </Typography>
      </Grid>
    );
  });

  const renderKeyBenefits = () => {
    if (hasOnlyUseCasesSummary) {
      return (
        <>
          {listing.benefits && (
            <Grid item={true} xs={12}>
              <Typography className={classes.questionTitle}>
                Key benefits of using {listing.name}
              </Typography>
              <HtmlCollapsibleText
                text={listing.benefits.replace(/\n/g, '<br/>')}
                maxNumberChars={200}
                variant="body-xs"
                onButtonClick={(_, { isExpanded }) => {
                  sendEvent({
                    name: 'engagement_product_click',
                    engagementComponent: 'product_overview',
                    engagementDestination: isExpanded
                      ? 'expand_key-benefits'
                      : 'collapse_key-benefits',
                    engagementDetails: isExpanded
                      ? 'button_read-more'
                      : 'button_read-less',
                  });
                }}
              />
            </Grid>
          )}
          {renderKeyTakeaways}
        </>
      );
    }

    if (listing.benefits) {
      if (keyTakeawaySummary) {
        return (
          <Grid item={true} xs={12}>
            <Typography className={classes.questionTitle}>
              Key benefits of using {listing.name}
            </Typography>
            <HtmlCollapsibleText
              text={listing.benefits.replace(/\n/g, '<br/>')}
              maxNumberChars={200}
              variant="body-xs"
              onButtonClick={(_, { isExpanded }) => {
                sendEvent({
                  name: 'engagement_product_click',
                  engagementComponent: 'product_overview',
                  engagementDestination: isExpanded
                    ? 'expand_key-benefits'
                    : 'collapse_key-benefits',
                  engagementDetails: isExpanded
                    ? 'button_read-more'
                    : 'button_read-less',
                });
              }}
            />
          </Grid>
        );
      } else {
        return (
          <Grid item={true} xs={12}>
            <CollapsibleContent
              always={true}
              className={classes.collapsibleContent}
              maxHeight={
                down('md') ? MOBILE_BENEFITS_MAX_HEIGHT : maxHeight.benefits
              }
              expanded={showMore.benefits}
              onChange={() => {
                sendEvent({
                  name: 'engagement_product_click',
                  engagementComponent: 'product_overview',
                  engagementDestination: showMore.benefits
                    ? 'collapse_key-benefits'
                    : 'expand_key-benefits',
                  engagementDetails: showMore.benefits
                    ? 'button_read-less'
                    : 'button_read-more',
                });
                toggleShowMore('benefits');
              }}
              showText={showMore.benefits ? 'Read less' : 'Read more'}
            >
              <Typography className={classes.questionTitle}>
                Key benefits of using {listing.name}
              </Typography>
              <Html Component="div" className={classes.text}>
                {listing.benefits.replace(/\n/g, '<br/>')}
              </Html>
            </CollapsibleContent>
          </Grid>
        );
      }
    }

    return '';
  };

  const overviewText =
    keyTakeaway && keyTakeaway.summary ? keyTakeaway.summary : listing.overview;
  const overviewLines = overviewText.split('\n');
  const overview = overviewLines.map((x, i) => (
    // eslint-disable-next-line react/no-array-index-key
    <Typography
      key={i}
      style={{ marginBottom: i === overviewLines.length - 1 ? 0 : '8px' }}
    >
      {x}
    </Typography>
  ));

  const shouldShowAvatar =
    (hasKeyTakeawaySummary &&
      !listing.benefits &&
      !hasOnlyUseCasesSummary &&
      !hasKeyTakeawayAiSummary) ||
    (hasKeyTakeawaySummary && hasOnlyUseCasesSummary && !listing.benefits) ||
    (hasKeyTakeawaySummary &&
      !hasOnlyUseCasesSummary &&
      hasKeyTakeawayAiSummary);

  return (
    <Card className={classes.root}>
      <Grid container={true} spacing={8}>
        <Grid item={true} lg={7} xs={12}>
          <Grid container={true} spacing={3}>
            <Grid item={true} xs={12}>
              <Typography className={classes.title}>
                {listing.name} overview
              </Typography>
              {shouldShowAvatar && (
                <div className={classes.contributors}>
                  {reviewerUrls.filter((item) => item !== null).length !==
                    0 && (
                    <Avatars
                      imageSize={24}
                      imagesUrl={reviewerUrls}
                      className={classes.avatars}
                    />
                  )}
                  <Typography
                    variant="tight-xs"
                    className={classes.reviewsTitle}
                  >
                    Based on {listing.num_reviews} verified user reviews
                  </Typography>
                </div>
              )}
            </Grid>

            {hasKeyTakeaways ? (
              <Grid item={true} xs={12}>
                <div className={classes.keyTakeawayContainer}>
                  <Typography
                    variant="heading-md"
                    className={classes.questionTitle}
                  >
                    What is {listing.name}?
                  </Typography>
                  {hasKeyTakeawaySummary && (
                    <Link
                      variant="tight-xxs"
                      onClick={() => {
                        sendEvent({
                          name: 'engagement_product_click',
                          engagementComponent: 'product_overview',
                          engagementDetails: `textlink_see-key-features-details`,
                          engagementDestination: `product_key-features`,
                        });
                        handleScrollTo('key-features', 100);
                      }}
                    >
                      See key features
                    </Link>
                  )}
                </div>
                <CollapsibleText
                  text={overviewText}
                  textVariant="body-xs"
                  maxNumberChars={LIMIT_OVERVIEW_LENGTH}
                  onButtonClick={(_, { isExpanded }) => {
                    sendEvent({
                      name: 'engagement_product_click',
                      engagementComponent: 'product_overview',
                      engagementDestination: isExpanded
                        ? 'expand_product-description'
                        : 'collapse_product-description',
                      engagementDetails: isExpanded
                        ? 'button_read-more'
                        : 'button_read-less',
                    });
                  }}
                />
              </Grid>
            ) : (
              <Grid item={true} xs={12}>
                <CollapsibleContent
                  always={true}
                  className={classes.collapsibleContent}
                  maxHeight={
                    down('md') ? MOBILE_OVERVIEW_MAX_HEIGHT : maxHeight.overview
                  }
                  expanded={showMore.overview}
                  onChange={() => {
                    toggleShowMore('overview');
                    sendEvent({
                      name: 'engagement_product_click',
                      engagementComponent: 'product_overview',
                      engagementDestination: showMore.overview
                        ? 'collapse_product-description'
                        : 'expand_product-description',
                      engagementDetails: showMore.overview
                        ? 'button_read-less'
                        : 'button_read-more',
                    });
                  }}
                  showText={showMore.overview ? 'Read less' : 'Read more'}
                >
                  <Typography className={classes.questionTitle}>
                    What is {listing.name}?
                  </Typography>
                  <div className={classes.overviewContainer}>{overview}</div>
                </CollapsibleContent>
              </Grid>
            )}
            {down('md') && (
              <Grid item={true} xs={12}>
                <div className={classes.containerWithBackground}>
                  <div className={classes.leftColumn}>
                    <Pricing
                      listing={listing}
                      showPplPricingBanner={showPplPricingBanner}
                    />

                    {!listing.is_premium && hasProsAndCons && (
                      <Alternatives
                        isCarousel={true}
                        subtitle="with better value for money"
                        alternatives={
                          listing.is_premium
                            ? listing.alternatives_comparisons
                            : listing.alternatives_by_section.faq_contenders
                        }
                      />
                    )}
                  </div>
                  {down('xs') && <Divider className={classes.divider} />}
                  <div className={classes.rightColumn}>
                    {hasProsAndCons ? (
                      <ProsAndCons
                        pros={pros}
                        cons={cons}
                        reviewsTotal={listing.reviews.total}
                        shouldShowAvatar={!shouldShowAvatar}
                      />
                    ) : (
                      <Alternatives
                        subtitle={
                          listing.is_premium
                            ? ''
                            : 'with better value for money'
                        }
                        alternatives={
                          listing.is_premium
                            ? listing.alternatives_comparisons
                            : listing.alternatives_by_section.faq_contenders
                        }
                      />
                    )}
                  </div>
                </div>
              </Grid>
            )}

            {hasKeyTakeaways &&
            !hasOnlyUseCasesSummary &&
            keyTakeaway.key_takeaways.length > 0
              ? renderKeyTakeaways
              : renderKeyBenefits()}
          </Grid>
        </Grid>
        {up('lg') && (
          <Grid item={true} lg={5} xs={12}>
            <div
              className={classes.containerWithBackground}
              ref={rightColumnRef}
            >
              <Pricing
                listing={listing}
                showPplPricingBanner={showPplPricingBanner}
              />
              {!listing.is_premium && hasProsAndCons && (
                <Alternatives
                  isCarousel={true}
                  subtitle="with better value for money"
                  alternatives={
                    listing.alternatives_by_section.faq_contenders ??
                    listing.alternatives_comparisons
                  }
                />
              )}

              {hasProsAndCons ? (
                <>
                  <Divider className={classes.divider} />
                  <ProsAndCons
                    pros={pros}
                    cons={cons}
                    reviewsTotal={listing.reviews.total}
                    shouldShowAvatar={!shouldShowAvatar}
                  />
                </>
              ) : (
                <>
                  {alternatives.length > 0 && (
                    <Divider className={classes.divider} />
                  )}
                  <Alternatives
                    subtitle={
                      listing.is_premium ? '' : 'with better value for money'
                    }
                    alternatives={alternatives}
                  />
                </>
              )}
            </div>
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

export default KeyTakeaways;
